import type { ShippingMethodQuoteFragment } from '#graphql-operations'

/**
 * Saturday delivery: who gets it, and how we recognise them.
 *
 * Zásilkovna delivers on Saturday in Prague, Brno and Ostrava. Nobody else does,
 * and nowhere else does — so the promise is only ever made when the destination
 * city and the carrier both check out. Everything here is a *destination* test;
 * the calendar half lives in `useDeliveryEstimate`, which takes the answer as a
 * flag and never works it out for itself.
 *
 * This list is not derived from anything. Unlike `constants/marketPolicy.ts`,
 * whose figures are read off the Vendure courier configuration and re-checkable
 * with `verify-market-policy.ts`, the backend models no delivery days at all —
 * couriers carry a carrier id and a branches flag, nothing about *when* they
 * deliver. So this is a hand-written commercial arrangement, and the only way to
 * withdraw it is a deploy. If it starts changing, it wants a courier custom
 * field rather than more entries here.
 */

/**
 * Cities Zásilkovna delivers to on a Saturday, as normalised name tokens.
 *
 * Matched against *any* token of the city, which is deliberately generous,
 * because the field is free text: the checkout's city input has no autocomplete
 * (`schemas/index.ts` asks only for a non-empty string) and a saved address is
 * whatever the customer once typed. Prague addresses in particular almost never
 * read "Praha" — they read "Praha 4", "Praha 9 - Letňany", "PRAHA". Ostrava's
 * districts put the name second ("Moravská Ostrava", "Slezská Ostrava"), which
 * is why every token is searched rather than only the first.
 *
 * The surrounding districts count too — Praha-východ, Praha-západ, Brno-venkov
 * and the rest. Those are administratively separate from the cities they are
 * named after, so this deliberately reaches past the city limits into towns
 * like Říčany and Jesenice.
 *
 * Whole tokens, never substrings: Ostravice is a village in the Beskydy
 * mountains with nothing to do with Ostrava, and a substring test would quietly
 * promise it a Saturday.
 *
 * Postal codes are deliberately NOT the key. Only Prague has a clean prefix
 * (1xx xx); 6xx xx is the whole South Moravian region and 7xx xx the whole
 * Moravian-Silesian one, so a prefix rule would be both wider than this and
 * harder to read.
 */
const SATURDAY_DELIVERY_CITIES = new Set(['praha', 'prague', 'brno', 'ostrava'])

/**
 * City string → comparable tokens. Same normalisation as `layers/common`'s
 * `createSlug` (NFD, drop the combining marks, break on everything else), so
 * "Brno-Střed", "BRNO STRED" and "brno střed" all yield ["brno", "stred"].
 */
function cityTokens(city: string): string[] {
  return city
    .normalize('NFD')
    .replace(/[\u0300-\u036F]/g, '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
}

/** Whether this city is one Zásilkovna delivers to on a Saturday. */
export function isSaturdayDeliveryCity(city?: string | null): boolean {
  if (!city)
    return false

  return cityTokens(city).some(token => SATURDAY_DELIVERY_CITIES.has(token))
}

/**
 * Whether this quote is carried by Zásilkovna.
 *
 * Read off `courier.code`, which is the courier's own ABRA `kod` and the
 * authoritative identity in both repos: `ZASILKOVNA`, `ZASILKOVNA_CZ`,
 * `ZASILKOVNA_IN_POST`, `ZASILKOVNA_POCZTA_POLSKA`. The shipping-method code
 * (`CZ_PP_ZASILKOVNA_CZ`) carries the same name, but only by the convention
 * that method codes are `<country>_<type>_<courier>`, and recovering it means
 * `split('_').slice(2)`.
 *
 * NOT `getPickupInfo(...).provider === 'zasilkovna'`, which looks like the
 * obvious test and is wrong twice over: that flag is `courierConfig.zasilkovna`,
 * which the backend only fills in for the two sub-carriers with a Packeta
 * carrier id (`ZASILKOVNA_IN_POST`, `ZASILKOVNA_POCZTA_POLSKA`), and it is
 * additionally gated on `branches`, so Zásilkovna home delivery never has it.
 * Both plain CZ Zásilkovna methods would silently fail the test.
 *
 * Home delivery and pickup point both qualify: the ticket names a carrier, and
 * CZ runs both ("Balíček do boxu" and "Kurýr na adresu"). Narrowing to pickup
 * points is `code.split('_')[1] === 'PP'` on the *method* code.
 */
export function isSaturdayDeliveryCarrier(method?: ShippingMethodQuoteFragment | null): boolean {
  return !!method?.courier?.code?.toUpperCase().startsWith('ZASILKOVNA')
}

/**
 * The whole destination test, as the checkout knows it.
 *
 * `locale === 'cs'` is doing more work than it looks: it is the Czech storefront,
 * and it is also the only market with one working day of transit
 * (`useDeliveryEstimate.TRANSIT_WORKING_DAYS`), which is what makes a Friday
 * dispatch reach Saturday at all.
 */
export function qualifiesForSaturdayDelivery(input: {
  locale: string
  countryCode?: string | null
  city?: string | null
  method?: ShippingMethodQuoteFragment | null
}): boolean {
  return input.locale === 'cs'
    && input.countryCode?.toUpperCase() === 'CZ'
    && isSaturdayDeliveryCity(input.city)
    && isSaturdayDeliveryCarrier(input.method)
}
